import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, BugAntIcon, BeakerIcon, CloudIcon } from '@heroicons/react/24/outline';

const CropProtection = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-green-900/80 to-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/images/services/crop_protection.png')}
                        alt="Crop Protection"
                        className="w-full h-full object-cover opacity-60"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-4xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-thin mb-6"
                    >
                        Crop <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">Protection</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light max-w-2xl mx-auto"
                    >
                        Intelligent Crop Defense for Maximum Yield & Sustainable Growth
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-2/3"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <span className="font-semibold text-gray-900">Marvelous Agri Solution</span>, crop protection is not a reactive activity — it is a predictive, science-driven protection architecture designed to safeguard every stage of crop development.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-10 font-light">
                            We deliver an advanced, integrated crop protection system that combines biological solutions, precision monitoring, preventive advisory, and responsible chemical interventions to ensure healthier crops, higher yields, and reduced environmental impact.
                        </p>

                        <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-green-500 mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                                🚀 Our Crop Protection Framework
                            </h3>
                            <div className="space-y-8">
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🔍 Predictive Pest & Disease Intelligence
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Using field data, climatic patterns, and crop-stage analysis, we identify potential pest and disease risks before they become economic threats.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🛡 Integrated Pest & Disease Management (IPM & IDM)
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        A balanced strategy that integrates biological control agents, targeted crop protection products, cultural practices, and need-based application models to minimize resistance and maximize effectiveness.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        🌱 Residue-Conscious Protection
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        We focus on safe, scientifically guided applications that help farmers move toward residue-managed and export-quality production.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                                        💧 Precision Application Strategy
                                    </h4>
                                    <p className="text-gray-600 font-light leading-relaxed">
                                        Right product. Right dose. Right time. Optimizing input use while reducing unnecessary chemical exposure.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 mb-6">🌍 Sustainable & Profitable Protection</h3>
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            Our crop protection solutions are engineered to:
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-600 text-lg font-light mb-8">
                            <li>Increase crop health and plant immunity</li>
                            <li>Reduce crop loss and yield variability</li>
                            <li>Improve produce quality and market value</li>
                            <li>Lower input wastage and production costs</li>
                            <li>Support environmentally responsible farming</li>
                        </ul>
                        <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50 rounded-r-2xl mb-10">
                            <p className="text-xl font-medium text-gray-800 italic">
                                We don’t just control pests. We design resilient crop ecosystems.
                            </p>
                        </div>

                        <div className="bg-gray-900 p-8 rounded-2xl shadow-xl text-white mb-10">
                            <h3 className="text-2xl font-bold mb-8 flex items-center gap-2">
                                💡 What Makes Our Crop Protection Different?
                            </h3>
                            <ul className="space-y-4">
                                {[
                                    "Data-driven advisory instead of guesswork",
                                    "Integrated biological + chemical balance",
                                    "Stage-specific crop health planning",
                                    "Climate-responsive protection strategies",
                                    "Farmer-focused, profitability-oriented execution"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-center gap-4 text-gray-300 font-light text-lg">
                                        <div className="bg-green-500/20 p-1.5 rounded-full flex-shrink-0">
                                            <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 rounded-2xl border border-green-200/60 shadow-sm relative overflow-hidden">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-green-200/40 rounded-full blur-3xl"></div>
                            <h3 className="text-2xl font-bold text-green-900 mb-4 relative z-10">🌱 Protect More Than Crops. Protect Potential.</h3>
                            <p className="text-green-800 text-lg leading-relaxed font-light mb-6 relative z-10">
                                With Marvelous Agri Solution, crop protection becomes a strategic advantage — strengthening plant vitality, improving economic returns, and ensuring long-term agricultural sustainability.
                            </p>
                            <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-700 to-emerald-600 relative z-10">
                                Smarter Protection. Stronger Crops. Sustainable Profits.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="lg:w-1/3 sticky top-24"
                    >
                        <div className="bg-white p-2 rounded-2xl shadow-2xl skew-y-2 transform hover:skew-y-0 transition-transform duration-500">
                            <img
                                src={require('../../../assets/images/services/crop_protection.png')}
                                alt="Crop Protection"
                                className="rounded-xl w-full h-auto object-cover"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CropProtection;
